import React from 'react';
import {Dialog, DialogBackdrop, DialogPanel, DialogTitle} from '@headlessui/react';
import Button from '@/components/ui/Button';
import ConfirmModal from '@/components/ui/ConfirmModal';
import {SquadReviewAction, SquadReviewPlayer} from '@/features/players/api';

interface SquadReviewDialogProps {
  isOpen: boolean;
  players: SquadReviewPlayer[];
  selected: Set<string>;
  pendingAction?: SquadReviewAction;
  errorMessage?: string;
  onClose: () => void;
  onTogglePlayer: (playerId: string, checked: boolean) => void;
  onToggleAll: (checked: boolean) => void;
  onKeepActive: () => void;
  onArchive: () => void;
}

const SquadReviewDialog: React.FC<SquadReviewDialogProps> = ({
  isOpen,
  players,
  selected,
  pendingAction,
  errorMessage,
  onClose,
  onTogglePlayer,
  onToggleAll,
  onKeepActive,
  onArchive,
}) => {
  const [confirmingArchive, setConfirmingArchive] = React.useState(false);
  const selectAllRef = React.useRef<HTMLInputElement>(null);
  const allSelected = players.length > 0 && selected.size === players.length;
  const someSelected = selected.size > 0 && !allSelected;

  React.useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = someSelected;
  }, [someSelected]);

  return (
    <>
      <Dialog open={isOpen} onClose={() => { if (!pendingAction) onClose(); }} className="relative z-50">
        <DialogBackdrop className="fixed inset-0 bg-black/50" />
        <div className="fixed inset-0 overflow-y-auto p-4">
          <div className="flex min-h-full items-center justify-center">
            <DialogPanel className="flex max-h-[calc(100vh-2rem)] w-full max-w-4xl flex-col rounded-xl bg-white p-5 shadow-xl dark:bg-gray-900 sm:p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <DialogTitle className="text-xl font-bold text-gray-900 dark:text-gray-100">Review missing players</DialogTitle>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">Choose how to handle players missing from the current Wyscout squad.</p>
                </div>
                <Button size="sm" variant="outline" disabled={Boolean(pendingAction)} onClick={onClose}>Close</Button>
              </div>

              {errorMessage && <p role="alert" className="mt-4 text-sm text-danger-700 dark:text-danger-300">{errorMessage}</p>}

              <div className="mt-4 min-h-0 overflow-y-auto">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[36rem] text-left text-sm" aria-label="Players awaiting squad review">
                    <thead className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      <tr>
                        <th scope="col" className="py-2 pr-3">
                          <input
                            ref={selectAllRef}
                            type="checkbox"
                            aria-label="Select all players"
                            checked={allSelected}
                            disabled={Boolean(pendingAction)}
                            onChange={() => onToggleAll(!allSelected)}
                          />
                        </th>
                        <th scope="col" className="py-2 pr-3">Player</th>
                        <th scope="col" className="py-2 pr-3">Position</th>
                        <th scope="col" className="py-2 pr-3">Wyscout status</th>
                        <th scope="col" className="py-2">Reason</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 text-gray-700 dark:divide-gray-800 dark:text-gray-200">
                      {players.map(player => (
                        <tr key={player.id}>
                          <td className="py-3 pr-3">
                            <input
                              type="checkbox"
                              aria-label={`Select ${player.name}`}
                              checked={selected.has(player.id)}
                              disabled={Boolean(pendingAction)}
                              onChange={event => onTogglePlayer(player.id, event.target.checked)}
                            />
                          </td>
                          <th scope="row" className="py-3 pr-3 font-semibold text-gray-900 dark:text-gray-100">{player.name}</th>
                          <td className="py-3 pr-3">{player.position}</td>
                          <td className="py-3 pr-3">{player.wyscoutStatus}</td>
                          <td className="py-3">{player.reason?.replaceAll('_', ' ') ?? 'Not provided'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="mt-4 flex flex-col gap-3 border-t border-gray-200 pt-4 dark:border-gray-800 sm:flex-row sm:items-center sm:justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-300">{selected.size} selected</span>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Button variant="outline" disabled={selected.size === 0 || Boolean(pendingAction)} loading={pendingAction === 'keep_active'} loadingLabel="Keeping selected current" onClick={onKeepActive}>Keep selected current</Button>
                  <Button variant="danger" disabled={selected.size === 0 || Boolean(pendingAction)} loading={pendingAction === 'archive'} loadingLabel="Archiving selected" onClick={() => setConfirmingArchive(true)}>Archive selected</Button>
                </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
      <ConfirmModal
        isOpen={confirmingArchive}
        title={`Archive ${selected.size} ${selected.size === 1 ? 'player' : 'players'}?`}
        message="Their history is retained."
        tone="danger"
        loading={pendingAction === 'archive'}
        onClose={() => setConfirmingArchive(false)}
        onConfirm={() => {
          setConfirmingArchive(false);
          onArchive();
        }}
      />
    </>
  );
};

export default SquadReviewDialog;
